<template>
  <SidebarSettingsGroup
    title="Style & Animation"
    collapsible
    :element-type="element.type"
    data-test="graphics-rectangleSettings-styleAndAnimation"
  >
    <template
      v-if="hideOpacityControls"
    >
      <opacity-settings v-model="opacity" />
      <hr class="sidebar-divider" />
    </template>

    <div class="sidebar-form-group">
      <border-radius
        v-model:top-left="borderTopLeftRadius"
        v-model:top-right="borderTopRightRadius"
        v-model:bottom-left="borderBottomLeftRadius"
        v-model:bottom-right="borderBottomRightRadius"
      />
    </div>

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="sidebar-form-label">Background color</label>
      <label
        class="radio inline-radio"
        for="solid"
      >Solid
        <input
          id="solid"
          v-model="fillStyle"
          type="radio"
          value="solid"
          data-test="graphics-rectangleSettings-fillStyleSolid"
          data-entity="graphics-rectangleSettings-fillStyleSolid"
        >
        <div class="checkmark"></div>
      </label>
      <label
        class="radio inline-radio"
        for="gradient"
      >Gradient
        <input
          id="gradient"
          v-model="fillStyle"
          type="radio"
          value="gradient"
          data-test="graphics-rectangleSettings-fillStyleGradient"
          data-entity="graphics-rectangleSettings-fillStyleGradient"
        >
        <div class="checkmark"></div>
      </label>

      <div
        v-if="fillStyle == 'solid'"
        class="colorpicker text-center"
        data-test="graphics-rectangleSettings-fillStyleSolidColor"
      >
        <colorpicker
          v-model="backgroundColor"
          :is-mapped="checkMapped('backgroundColor')"
        />
        <span
          data-balloon="This setting is controlled by data. Unmap data to customize."
          data-balloon-pos="down"
          data-balloon-length="medium"
        >
          <span
            v-if="checkMapped('backgroundColor')"
            class="fa fa-lock mapped-lock"
          />
        </span>
      </div>

      <div
        v-else
        class="settings-row settings-row-xs"
        data-test="graphics-rectangleSettings-fillStyleGradient"
      >
        <div class="settings-col-6 settings-col-xs">
          <div class="input-group">
            <validation-field as="input"
              v-model.number="gradientDirection"
              rules="required|numeric"
              type="number"
              name="gradientDirection"
              :class="{ 'is-invalid': validationErrors['gradientDirection'] }"
              data-test="graphics-rectangleSettings-setGradientDirection"
              data-entity="graphics-rectangleSettings-setGradientDirection"
            />
            <span class="input-group-addon symbol">°</span>
          </div>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker text-center">
          <colorpicker
            v-model="gradientColorOne"
            :is-mapped="checkMapped('gradientColorOne')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('gradientColorOne')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>
        <div class="settings-col-3 settings-col-xs colorpicker text-center">
          <colorpicker
            v-model="gradientColorTwo"
            :is-mapped="checkMapped('gradientColorTwo')"
          />
          <span
            data-balloon="This setting is controlled by data. Unmap data to customize."
            data-balloon-pos="down-right"
            data-balloon-length="medium"
          >
            <span
              v-if="checkMapped('gradientColorTwo')"
              class="fa fa-lock mapped-lock"
            />
          </span>
        </div>
      </div>

      <!-- Map control -->
      <!-- TODO: remove once sheets can have more than one thing mapped -->
      <div
        v-if="showDataMapControls"
        class="margin-top-sm"
        data-test="graphics-rectangleSettings-fillStyleMapped"
      >
        <p
          v-if="isBackgroundColorMapped"
          class="sidebar-form-label margin-top-sm"
        >
          From Data Provider
        </p>

        <map-data-control
          v-if="fillStyle == 'solid'"
          :data-provider="dataProvider"
          :element="element"
          :scroll-offset="scrollOffset"
          property="backgroundColor"
          :mapped="checkMapped('backgroundColor')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
        <map-data-control
          v-if="fillStyle == 'gradient'"
          :data-provider="dataProvider"
          :scroll-offset="scrollOffset"
          :element="element"
          property="gradientColorOne"
          :mapped="checkMapped('gradientColorOne')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
        <map-data-control
          v-if="fillStyle == 'gradient'"
          :data-provider="dataProvider"
          :scroll-offset="scrollOffset"
          :element="element"
          property="gradientColorTwo"
          :mapped="checkMapped('gradientColorTwo')"
          @update-map="mapToField"
          @clear-map="clearMapping"
        />
      </div>
    </div>

    <hr class="sidebar-divider" />
    <background-image-settings
      v-if="!isSocialIcon"
      :element="element"
      :scroll-offset="scrollOffset"
      @modal="(opts) => $emit('modal', opts)"
    />
    <map-data-control
      v-if="isSocialIcon"
      :data-provider="dataProvider"
      :element="element"
      label="Network Icon Style"
      control-type="select"
      property="backgroundImageUrl"
      :mapped="checkMapped('backgroundImageUrl')"
      :no-unmap="true"
      @update-map="mapToField"
      @clear-map="clearMapping"
    />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Border
        <input
          v-model="border"
          type="checkbox"
          data-test="graphics-rectangleSettings-toggleBorder"
          data-entity="graphics-rectangleSettings-toggleBorder"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <template v-if="border">
        <div class="settings-row settings-row-xs">
          <div class="settings-col-6 settings-col-xs">
            <validation-field as="input"
              v-model.number="borderWidth"
              rules="required|numeric|min:0"
              type="number"
              name="borderWidth"
              min="0"
              :class="{ 'is-invalid': validationErrors['borderWidth'] }"
              data-test="graphics-rectangleSettings-setBorderWidth"
              data-entity="graphics-rectangleSettings-setBorderWidth"
            />
            <label class="sidebar-input-sub-label">Width</label>
          </div>
          <div class="settings-col-6 settings-col-xs colorpicker">
            <colorpicker
              v-model="borderColor"
              :is-mapped="checkMapped('borderColor')"
            />
            <label class="sidebar-input-sub-label">
              Color
              <span
                data-balloon="This setting is controlled by data. Unmap data to customize."
                data-balloon-pos="down-right"
                data-balloon-length="medium"
              >
                <span
                  v-if="checkMapped('borderColor')"
                  class="fa fa-lock mapped-lock"
                />
              </span>
            </label>
          </div>
        </div>
        <!-- TODO: remove once sheets can have more than one thing mapped -->
        <div v-if="showDataMapControls">
          <p
            v-if="checkMapped('borderColor')"
            class="sidebar-form-label margin-bottom-sm"
          >
            From Data Provider
          </p>
          <map-data-control
            v-if="showDataMapControls"
            :data-provider="dataProvider"
            :scroll-offset="scrollOffset"
            :element="element"
            property="borderColor"
            :mapped="checkMapped('borderColor')"
            @update-map="mapToField"
            @clear-map="clearMapping"
          />
        </div>
      </template>
    </div>

    <hr class="sidebar-divider" />

    <box-shadow-settings
      :element="element"
      :data-provider="dataProvider"
      :scroll-offset="scrollOffset"
    />

    <hr class="sidebar-divider" />

    <div class="sidebar-form-group">
      <label class="checkbox header-checkbox">Blur
        <input
          v-model="blur"
          type="checkbox"
          data-test="graphics-rectangleSettings-toggleBlur"
          data-entity="graphics-rectangleSettings-toggleBlur"
        />
        <div class="checkmark">
          <span class="checkmark-tick fa-solid fa-check"></span>
        </div>
      </label>

      <div
        v-if="blur"
        class="input-group"
        data-test="graphics-rectangleSettings-setBlurSize"
      >
        <validation-field as="input"
          v-model.number="blurSize"
          rules="required|numeric|min:0"
          type="number"
          name="blurSize"
          min="0"
          :class="{ 'is-invalid': validationErrors['blurSize'] }"
          data-test="graphics-rectangleSettings-setBlurSize"
          data-entity="graphics-rectangleSettings-setBlurSize"
        />
        <span class="input-group-addon">px</span>
      </div>
    </div>

    <hr class="sidebar-divider" />

    <animation-settings :element="element"></animation-settings>
  </SidebarSettingsGroup>
</template>

<script>
import SettingsMixins from '../../mixins/settings-mixins';

import AnimationSettings from './Animation.vue';
import OpacitySettings from './Opacity.vue';
import BoxShadowSettings from './BoxShadow.vue';
import BackgroundImageSettings from './BackgroundImage.vue';
import MapDataControl from './Data/MapDataControl.vue';
import BorderRadius from './BorderRadius.vue';

export default {
  emits: ['modal'],
  name: 'RectangleSettingsComponent',
  components: {
    AnimationSettings,
    OpacitySettings,
    BoxShadowSettings,
    BackgroundImageSettings,
    MapDataControl,
    BorderRadius,
  },
  mixins: [SettingsMixins],
  props: ['element', 'scrollOffset'],
  computed: {
    opacity: {
      get() {
        return this.getPropertyValue('styles.opacity', { emptyValue: 1 });
      },
      set(v) {
        this.updateOneElementValue('styles.opacity', v);
      },
    },
    hideOpacityControls() {
      return !this.isSocialIcon;
    },
    isSocialIcon() {
      return this.element.data?.type === 'social-network-attribution';
    },
    borderTopLeftRadius: {
      get() {
        return this.getPropertyValue('borderTopLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopLeftRadius', v);
      },
    },
    borderTopRightRadius: {
      get() {
        return this.getPropertyValue('borderTopRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderTopRightRadius', v);
      },
    },
    borderBottomRightRadius: {
      get() {
        return this.getPropertyValue('borderBottomRightRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomRightRadius', v);
      },
    },
    borderBottomLeftRadius: {
      get() {
        return this.getPropertyValue('borderBottomLeftRadius');
      },
      set(v) {
        this.updateOneElementValue('borderBottomLeftRadius', v);
      },
    },
    fillStyle: {
      get() {
        return this.getPropertyValue('fillStyle');
      },
      set(v) {
        this.updateOneElementValue('fillStyle', v);
      },
    },
    backgroundColor: {
      get() {
        return this.getPropertyValue('backgroundColor', { mappedPath: 'backgroundColor' });
      },
      set(v) {
        this.updateOneElementValue('backgroundColor', v);
      },
    },
    gradientDirection: {
      get() {
        return this.getPropertyValue('gradientDirection');
      },
      set(v) {
        this.updateOneElementValue('gradientDirection', v);
      },
    },
    gradientColorOne: {
      get() {
        return this.getPropertyValue('gradientColorOne', { mappedPath: 'gradientColorOne' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorOne', v);
      },
    },
    gradientColorTwo: {
      get() {
        return this.getPropertyValue('gradientColorTwo', { mappedPath: 'gradientColorTwo' });
      },
      set(v) {
        this.updateOneElementValue('gradientColorTwo', v);
      },
    },
    border: {
      get() {
        return this.getPropertyValue('border');
      },
      set(v) {
        this.updateOneElementValue('border', v);
      },
    },
    borderWidth: {
      get() {
        return this.getPropertyValue('borderWidth');
      },
      set(v) {
        this.updateOneElementValue('borderWidth', v);
      },
    },
    borderColor: {
      get() {
        return this.getPropertyValue('borderColor', { mappedPath: 'borderColor' });
      },
      set(v) {
        this.updateOneElementValue('borderColor', v);
      },
    },
    blur: {
      get() {
        return this.getPropertyValue('blur');
      },
      set(v) {
        this.updateOneElementValue('blur', v);
      },
    },
    blurSize: {
      get() {
        return this.getPropertyValue('blurSize');
      },
      set(v) {
        this.updateOneElementValue('blurSize', v);
      },
    },
  },
};
</script>
